import React, { useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Box, Button, Flex, Heading, HStack, Icon, Input, InputGroup, InputLeftElement, Menu, MenuButton, MenuItem, MenuList, SimpleGrid, Spinner, Switch, Text, VStack, useToast } from "@chakra-ui/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  PiUsersThreeDuotone, PiUserPlusDuotone, PiClockDuotone, PiChatCircleDotsDuotone, PiPlusBold,
  PiSealCheckDuotone, PiMagnifyingGlassBold, PiCaretDownBold, PiCaretRightBold, PiCheckCircleDuotone,
  PiBroadcastDuotone, PiSparkleDuotone, PiBuildingsDuotone, PiUserDuotone, PiFireDuotone, PiHeartDuotone
} from "react-icons/pi";
import {
  ApiVotingListItem,
  DiscoverFeed,
  DiscoverGroup,
  DiscoverProfile,
  DiscoverSort,
  FeedPost,
  FeedSidebarGroup,
  FeedSidebarVoting,
  followUser,
  getDiscoverFeed,
  getDiscoverGroups,
  getDiscoverProfiles,
  getGroupsWallFeed,
  getProfile,
  getPublicVotings,
  isLoggedIn,
  joinDiscoverGroup,
  unfollowUser,
  WallPost,
  VOTING_STATUSES
} from "@lubbezposrednio-frontend/core";
import { AdaptiveShell } from "../OrganizerDashboardView/components/AdaptiveShell";
import { VotingTile } from "../../components/VotingTile";
import WallPostCard from "../GroupWallView/WallPostCard";
import { mediaUrl } from "../GroupWallView/wallShared";
import { PortalComposer } from "./PortalComposer";
import { GLASS, SCROLL_FOG, drgba, EASE } from "../../components/glassUi";
import { FeedSkeleton, CardsGridSkeleton, RailSkeleton } from "../../components/skeletons";

type TabKey = "spolecznosc" | "glosowania" | "organizacje" | "grupy" | "ludzie";

const TABS: { key: TabKey; label: string; icon: React.ElementType }[] = [
  { key: "spolecznosc", label: "Społeczność", icon: PiChatCircleDotsDuotone },
  { key: "glosowania", label: "Głosowania", icon: PiBroadcastDuotone },
  { key: "organizacje", label: "Organizacje", icon: PiBuildingsDuotone },
  { key: "grupy", label: "Grupy", icon: PiUsersThreeDuotone },
  { key: "ludzie", label: "Ludzie", icon: PiUserDuotone }
];

const backend = (p?: string | null) => (p ? `${process.env.REACT_APP_BACKEND_URL}${p}` : undefined);

const votingToTile = (v: ApiVotingListItem) => {
  const u = v.user;
  const name = u ? (u.is_organization ? u.organization_name : `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim()) || u.email : "";
  return (
    <VotingTile
      key={v.id}
      id={v.id}
      title={v.title}
      status={v.status}
      startDate={v.start_date}
      endDate={v.end_date}
      votersCount={v.voters_count}
      questionsCount={v.questions_count}
      signupOpen={v.signup_open}
      organizer={name ? { name, avatarUrl: u?.avatar_url, profileRef: u?.profile_ref } : null}
    />
  );
};

// ── karta profilu (odkrywanie) ──
const ProfileCard: React.FC<{ p: DiscoverProfile }> = ({ p }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [following, setFollowing] = useState(!!p.followed_by_me);
  const [followers, setFollowers] = useState(p.followers_count);
  const [busy, setBusy] = useState(false);
  const loggedIn = isLoggedIn();

  const toggle = async () => {
    if (!loggedIn) {
      navigate("/logowanie");
      return;
    }
    setBusy(true);
    try {
      const r = following ? await unfollowUser(p.id) : await followUser(p.id);
      setFollowing(r.data.following);
      setFollowers(r.data.followers_count);
    } catch {
      toast({ title: "Nie udało się", status: "error", isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box borderRadius="20px" p="16px" sx={GLASS} transition={`transform ${EASE}, box-shadow ${EASE}`} _hover={{ transform: "translateY(-3px)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 22px 50px -22px rgba(43,0,90,0.5)" }}>
      <Box>
        <Flex justify="space-between" align="flex-start">
          <Avatar size="lg" name={p.name} src={backend(p.avatar_url)} bg="primary" color="white" cursor="pointer" onClick={() => navigate(`/profil/${p.profile_ref}`)} />
          <Button
            size="sm"
            onClick={toggle}
            isLoading={busy}
            leftIcon={<Icon as={following ? PiCheckCircleDuotone : PiUserPlusDuotone} />}
            bg={following ? drgba("#430098", 0.08) : "accent"}
            color={following ? "primary" : "white"}
            borderRadius="11px"
            fontWeight="700"
            _hover={{ bg: following ? drgba("#430098", 0.14) : "#c41a3a" }}
          >
            {following ? "Obserwujesz" : "Obserwuj"}
          </Button>
        </Flex>
        <HStack spacing="6px" mt="10px" cursor="pointer" onClick={() => navigate(`/profil/${p.profile_ref}`)} w="fit-content">
          <Text fontWeight="800" color="#241536" noOfLines={1}>{p.name}</Text>
          {p.verified && <Icon as={PiSealCheckDuotone} color="#1d9bf0" boxSize="16px" />}
        </HStack>
        <HStack spacing="6px" mt="2px" color="#a8a2b6" fontSize="12.5px" fontWeight="600">
          <Icon as={p.is_organization ? PiBuildingsDuotone : PiUserDuotone} boxSize="13px" />
          <Text noOfLines={1}>{p.profile_ref} · {followers} obserw.</Text>
        </HStack>
        {p.bio && <Text fontSize="13px" color="#6b6480" noOfLines={2} mt="7px">{p.bio}</Text>}
      </Box>
    </Box>
  );
};

// ── karta grupy (odkrywanie) ──
const GroupCard: React.FC<{ g: DiscoverGroup; compact?: boolean }> = ({ g, compact }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const loggedIn = isLoggedIn();
  const [state, setState] = useState<"none" | "member" | "pending">(g.is_member ? "member" : "none");
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(g.members_count);

  const join = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!loggedIn) {
      navigate("/logowanie");
      return;
    }
    setBusy(true);
    try {
      const r = await joinDiscoverGroup(g.id);
      setState(r.data.status === "member" ? "member" : "pending");
      if (r.data.status === "member") setCount(c => c + 1);
      toast({
        title: r.data.status === "member" ? "Dołączyłeś do grupy" : "Prośba wysłana — czeka na akceptację",
        status: "success",
        isClosable: true,
        duration: 2000
      });
    } catch {
      toast({ title: "Nie udało się dołączyć", status: "error", isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  const action =
    state === "member" ? (
      <Button size="sm" flexShrink={0} leftIcon={<Icon as={PiCheckCircleDuotone} />} bg={drgba("#430098", 0.08)} color="primary" borderRadius="11px" fontWeight="700" isDisabled>
        Należysz
      </Button>
    ) : state === "pending" ? (
      <Button size="sm" flexShrink={0} leftIcon={<Icon as={PiClockDuotone} />} bg={drgba("#f59e0b", 0.16)} color="#b7791f" borderRadius="11px" fontWeight="700" isDisabled>
        Oczekuje
      </Button>
    ) : (
      <Button size="sm" flexShrink={0} onClick={join} isLoading={busy} leftIcon={<Icon as={PiUserPlusDuotone} />} bg="accent" color="white" borderRadius="11px" fontWeight="700" _hover={{ bg: "#c41a3a" }}>
        Dołącz
      </Button>
    );

  // Wariant kompaktowy do rail — bez opisu.
  if (compact) {
    return (
      <HStack as={Link} to={`/organizator/grupy/${g.id}`} spacing="10px" align="center" py="7px" px="7px" borderRadius="12px" transition={`background ${EASE}`} _hover={{ bg: "rgba(24,12,48,0.04)", textDecoration: "none" }}>
        <Avatar size="sm" boxSize="34px" borderRadius="10px" name={g.name} src={backend(g.avatar_url)} bg={drgba("#430098", 0.1)} color="primary" icon={<Icon as={PiUsersThreeDuotone} boxSize="17px" color="primary" />} />
        <Box flex="1" minW={0}>
          <Text fontWeight="700" color="#2f2a3d" fontSize="13.5px" noOfLines={1}>{g.name}</Text>
          <Text fontSize="11.5px" color="#a8a2b6" fontWeight="600" noOfLines={1}>{count} {count === 1 ? "członek" : "członków"}</Text>
        </Box>
        {action}
      </HStack>
    );
  }

  return (
    <Box
      as={Link}
      to={`/organizator/grupy/${g.id}`}
      display="block"
      borderRadius="20px"
      p="17px 19px"
      sx={GLASS}
      transition={`transform ${EASE}, box-shadow ${EASE}`}
      _hover={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 22px 50px -22px rgba(43,0,90,0.5)", transform: "translateY(-3px)", textDecoration: "none" }}
    >
      <Flex align="flex-start" gap="13px" mb="12px">
        <Avatar size="md" name={g.name} src={backend(g.avatar_url)} borderRadius="14px" bg={drgba("#430098", 0.1)} color="primary" flexShrink={0} icon={<Icon as={PiUsersThreeDuotone} boxSize="22px" color="primary" />} />
        <Box minW={0} flex="1">
          <Text fontWeight="800" color="#241536" fontSize="16px" noOfLines={1}>{g.name}</Text>
          <Text fontSize="13px" color="#6b6480" noOfLines={1} mt="2px">{g.description || "Grupa społeczności"}</Text>
        </Box>
      </Flex>

      {g.signup_open && (
        <HStack spacing="6px" w="fit-content" bg={drgba("#1f9e18", 0.12)} px="9px" py="3px" borderRadius="full" mb="10px">
          <Box w="6px" h="6px" borderRadius="full" bg="#2FCE25" />
          <Text fontSize="11px" color="#1f9e18" fontWeight="800" whiteSpace="nowrap">Zapisy otwarte</Text>
        </HStack>
      )}

      <Flex align="center" justify="space-between" gap="10px" pt="13px" borderTop="1px solid rgba(24,12,48,0.06)">
        <HStack spacing="6px" color="#6b6480" fontSize="13px" fontWeight="700" minW={0}>
          <Icon as={PiUsersThreeDuotone} boxSize="15px" color="#a8a2b6" />
          <Text noOfLines={1}>
            {count} {count === 1 ? "członek" : "członków"}
            {g.requires_approval ? " · akceptacja" : ""}
          </Text>
        </HStack>
        {action}
      </Flex>
    </Box>
  );
};

// Podtytuł zależny od zakładki
const TAB_SUB: Record<TabKey, string> = {
  spolecznosc: "Feed z Twoich grup i obserwowanych.",
  glosowania: "Publiczne głosowania — trwające i nadchodzące.",
  organizacje: "Odkrywaj organizacje i śledź ich głosowania.",
  grupy: "Publiczne grupy, do których możesz dołączyć.",
  ludzie: "Odkrywaj i obserwuj ludzi ze społeczności."
};

// Nagłówek huba — czysty glass (bez gradientowego banera) + pigułki-taby, spójnie z widokiem grup.
const HubHero: React.FC<{
  tab: TabKey;
  onTab: (t: TabKey) => void;
  search?: { value: string; onChange: (v: string) => void };
  sort?: { value: DiscoverSort; onChange: (v: DiscoverSort) => void };
}> = ({ tab, onTab, search, sort }) => (
  <Box borderRadius="24px" sx={GLASS}>
    <Box px={{ base: "18px", md: "26px" }} pt={{ base: "18px", md: "22px" }} pb="14px">
      <HStack spacing="14px" minW={0}>
        <Flex w={{ base: "50px", md: "56px" }} h={{ base: "50px", md: "56px" }} borderRadius="16px" bg={drgba("#7b2ff7", 0.12)} align="center" justify="center" flexShrink={0}>
          <Icon as={PiUsersThreeDuotone} boxSize="28px" color="#7b2ff7" />
        </Flex>
        <Box minW={0}>
          <Heading fontSize={{ base: "24px", md: "28px" }} fontWeight="900" color="#1b1030" lineHeight="1.05" letterSpacing="-0.02em">Społeczność</Heading>
          <Text fontSize="13.5px" color="#6b6480" mt="3px" noOfLines={1}>{TAB_SUB[tab]}</Text>
        </Box>
      </HStack>

      {/* Pigułki-taby */}
      <HStack spacing="3px" mt="16px" pt="12px" borderTop="1px solid rgba(24,12,48,0.07)" overflowX="auto" sx={{ scrollbarWidth: "none", "::-webkit-scrollbar": { display: "none" } }}>
        {TABS.map(t => {
          const isActive = t.key === tab;
          return (
            <Button key={t.key} onClick={() => onTab(t.key)} leftIcon={<Icon as={t.icon} boxSize="15px" />} size="sm" borderRadius="full" fontWeight="800" px="15px"
              color={isActive ? "#7b2ff7" : "#6b6480"} bg={isActive ? "rgba(123,47,247,0.1)" : "transparent"}
              boxShadow="none"
              _hover={{ bg: isActive ? "rgba(123,47,247,0.16)" : "rgba(24,12,48,0.06)" }} flexShrink={0}>
              {t.label}
            </Button>
          );
        })}
      </HStack>

      {(search || sort) && (
        <Flex mt="12px" gap="8px" align="center">
          {search && (
            <InputGroup size="sm" flex="1">
              <InputLeftElement pointerEvents="none"><Icon as={PiMagnifyingGlassBold} color="#a8a2b6" /></InputLeftElement>
              <Input value={search.value} onChange={e => search.onChange(e.target.value)} placeholder="Szukaj" borderRadius="11px" bg="rgba(255,255,255,0.72)" border="1px solid rgba(24,12,48,0.08)" _focusVisible={{ borderColor: drgba("#430098", 0.4), boxShadow: `0 0 0 3px ${drgba("#430098", 0.12)}` }} />
            </InputGroup>
          )}
          {sort && (
            <Menu>
              <MenuButton as={Button} size="sm" variant="outline" borderColor="rgba(67,0,152,0.18)" color="#6b6480" borderRadius="11px" fontWeight="700" flexShrink={0} rightIcon={<Icon as={PiCaretDownBold} boxSize="12px" />}>
                {sort.value === "popular" ? "Popularne" : "Nowe"}
              </MenuButton>
              <MenuList minW="160px" borderRadius="12px">
                <MenuItem onClick={() => sort.onChange("popular")} fontWeight={sort.value === "popular" ? "700" : "500"}>Popularne</MenuItem>
                <MenuItem onClick={() => sort.onChange("new")} fontWeight={sort.value === "new" ? "700" : "500"}>Nowe</MenuItem>
              </MenuList>
            </Menu>
          )}
        </Flex>
      )}
    </Box>
  </Box>
);

// Nagłówek sekcji w rail
const RailHeading: React.FC<{ icon: React.ElementType; title: string; color?: string; action?: React.ReactNode }> = ({ icon, title, color = "#430098", action }) => (
  <Flex justify="space-between" align="center" mb="11px">
    <HStack spacing="8px">
      <Icon as={icon} boxSize="18px" color={color} />
      <Text fontSize="12px" fontWeight="800" color="#5a5468" textTransform="uppercase" letterSpacing="0.08em">{title}</Text>
    </HStack>
    {action}
  </Flex>
);

const MyGroupRow: React.FC<{ g: FeedSidebarGroup }> = ({ g }) => (
  <HStack as={Link} to={`/organizator/grupy/${g.id}`} spacing="10px" py="6px" px="6px" borderRadius="11px" transition={`background ${EASE}`} _hover={{ bg: "rgba(24,12,48,0.04)", textDecoration: "none" }}>
    <Avatar size="sm" boxSize="34px" borderRadius="10px" name={g.name} src={mediaUrl(g.avatar_url)} bg={drgba("#430098", 0.1)} color="primary" icon={<Icon as={PiUsersThreeDuotone} boxSize="17px" />} />
    <Box minW={0} flex="1">
      <Text fontSize="13.5px" fontWeight="700" color="#2f2a3d" noOfLines={1}>{g.name}</Text>
      <Text fontSize="11.5px" color="#a8a2b6" fontWeight="600">{g.members_count} {g.members_count === 1 ? "członek" : "członków"}</Text>
    </Box>
    <Box px="7px" py="1px" borderRadius="full" bg={g.role === "owner" ? drgba("#7b2ff7", 0.14) : "rgba(24,12,48,0.06)"} flexShrink={0}>
      <Text fontSize="9.5px" fontWeight="800" color={g.role === "owner" ? "#6b21d6" : "#8a8397"} textTransform="uppercase" letterSpacing="0.03em">{g.role === "owner" ? "Zarządzasz" : "Należysz"}</Text>
    </Box>
  </HStack>
);

const SideVotingRow: React.FC<{ v: FeedSidebarVoting }> = ({ v }) => (
  <HStack as={Link} to={`/glosowanie/${v.id}`} justify="space-between" py="7px" px="8px" borderRadius="11px" transition={`background ${EASE}`} _hover={{ bg: "rgba(24,12,48,0.04)", textDecoration: "none" }}>
    <HStack spacing="8px" minW={0}>
      <Icon as={PiBroadcastDuotone} boxSize="15px" color={v.status === "ongoing" ? "#1f9e18" : "#7b2ff7"} flexShrink={0} />
      <Text fontSize="13px" fontWeight="600" color="#2f2a3d" noOfLines={1}>{v.title}</Text>
    </HStack>
    <Text fontSize="11px" fontWeight="800" color={v.status === "ongoing" ? "#1f9e18" : "#7b2ff7"} flexShrink={0}>{v.status === "ongoing" ? "Trwa" : "Wkrótce"}</Text>
  </HStack>
);

const VSTAT: Record<string, { label: string; color: string }> = {
  ongoing: { label: "Trwa", color: "#1f9e18" },
  planned: { label: "Wkrótce", color: "#7b2ff7" },
  finished: { label: "Wyniki", color: "#2563eb" },
  draft: { label: "Szkic", color: "#8a8397" }
};

// Wiersz głosowania w rail (na czasie / od obserwowanych) — z awatarem organizatora.
const RailVotingRow: React.FC<{ v: ApiVotingListItem }> = ({ v }) => {
  const s = VSTAT[v.status ?? ""] ?? { label: "", color: "#7b2ff7" };
  const u = v.user;
  const orgName = u ? (u.is_organization ? u.organization_name : `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim()) || u.email : "";
  return (
    <HStack as={Link} to={`/glosowanie/${v.id}`} spacing="10px" py="7px" px="8px" borderRadius="11px" transition={`background ${EASE}`} _hover={{ bg: "rgba(24,12,48,0.04)", textDecoration: "none" }}>
      <Avatar size="sm" boxSize="30px" borderRadius="9px" name={orgName || v.title} src={backend(u?.avatar_url)} bg={drgba("#430098", 0.1)} color="primary" icon={<Icon as={PiBroadcastDuotone} boxSize="15px" color="primary" />} flexShrink={0} />
      <Box flex="1" minW={0}>
        <Text fontSize="13px" fontWeight="700" color="#2f2a3d" noOfLines={1}>{v.title}</Text>
        {orgName && <Text fontSize="11px" color="#a8a2b6" fontWeight="600" noOfLines={1}>{orgName}</Text>}
      </Box>
      {s.label && <Text fontSize="10.5px" fontWeight="800" color={s.color} flexShrink={0}>{s.label}</Text>}
    </HStack>
  );
};

// Mini-karta „Twój profil" na górze rail
const ProfileMini: React.FC<{ name: string; avatarUrl?: string; verified: boolean; profileRef?: string; isOrg: boolean }> = ({ name, avatarUrl, verified, profileRef, isOrg }) => (
  <HStack as={Link} to={profileRef ? `/profil/${profileRef}` : "/organizator/konto"} spacing="11px" p="9px" borderRadius="15px" bg="rgba(255,255,255,0.5)" border="1px solid rgba(24,12,48,0.06)" transition={`background ${EASE}`} _hover={{ bg: drgba("#430098", 0.06), textDecoration: "none" }}>
    <Avatar size="sm" boxSize="42px" name={name || "?"} src={avatarUrl} bg="primary" color="white" flexShrink={0} />
    <Box flex="1" minW={0}>
      <HStack spacing="5px" minW={0}>
        <Text fontSize="14px" fontWeight="800" color="#241536" noOfLines={1}>{name || "Twoje konto"}</Text>
        {verified && <Icon as={PiSealCheckDuotone} boxSize="14px" color="#1d9bf0" flexShrink={0} />}
      </HStack>
      <HStack spacing="5px" color="#a8a2b6" fontSize="11.5px" fontWeight="600" minW={0}>
        <Icon as={isOrg ? PiBuildingsDuotone : PiUserDuotone} boxSize="12px" flexShrink={0} />
        <Text noOfLines={1}>{profileRef ? `@${profileRef.replace(/^@+/, "")}` : "Profil niepubliczny"}</Text>
      </HStack>
    </Box>
    <Icon as={PiCaretRightBold} boxSize="13px" color="#c9c3d6" flexShrink={0} />
  </HStack>
);

const DiscoverView: React.FC = () => {
  const loggedIn = isLoggedIn();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: TabKey = (TABS.find(t => t.key === searchParams.get("sekcja"))?.key ?? "spolecznosc") as TabKey;
  const setTab = (k: TabKey) =>
    setSearchParams(
      prev => {
        const p = new URLSearchParams(prev);
        if (k === "spolecznosc") p.delete("sekcja");
        else p.set("sekcja", k);
        return p;
      },
      { replace: true }
    );
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [sort, setSort] = useState<DiscoverSort>("popular");

  // Odkrywanie
  const [suggest, setSuggest] = useState<DiscoverFeed | null>(null);
  const [votings, setVotings] = useState<ApiVotingListItem[]>([]);
  const [profiles, setProfiles] = useState<DiscoverProfile[]>([]);
  const [groups, setGroups] = useState<DiscoverGroup[]>([]);
  const [loading, setLoading] = useState(true);

  // Feed Społeczności (zagregowana ściana)
  const [feedPosts, setFeedPosts] = useState<FeedPost[]>([]);
  const [myGroups, setMyGroups] = useState<FeedSidebarGroup[]>([]);
  const [activeVotings, setActiveVotings] = useState<FeedSidebarVoting[]>([]);
  const [feedCursor, setFeedCursor] = useState<string | null>(null);
  const [feedHasMore, setFeedHasMore] = useState(false);
  const [feedLoadingMore, setFeedLoadingMore] = useState(false);
  const [feedBusy, setFeedBusy] = useState(false); // odświeżanie feedu po zmianie filtrów (bez globalnego loadingu)
  const firstFilter = useRef(true);
  const [feedScope, setFeedScope] = useState<"community" | "following">("community");
  const [includeGroups, setIncludeGroups] = useState(true);
  const [includeTopics, setIncludeTopics] = useState(true);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef(false);

  const [me, setMe] = useState<{ canPost: boolean; verified: boolean; name: string; avatarUrl?: string; profileRef?: string; isOrg: boolean }>({ canPost: false, verified: false, name: "", isOrg: false });

  useEffect(() => {
    document.title = "Społeczność";
    getProfile()
      .then(r => {
        const u = r.data?.user;
        setMe({
          canPost: u?.can_post_portal ?? !!u?.verified,
          verified: !!u?.verified,
          name: u?.is_organization ? u?.organization_name || "" : u?.first_name || "",
          avatarUrl: u?.avatar_url ? `${process.env.REACT_APP_BACKEND_URL}${u.avatar_url}` : undefined,
          profileRef: u?.profile_public && u?.profile_ref ? u.profile_ref : undefined,
          isOrg: !!u?.is_organization
        });
      })
      .catch(() => undefined);
    // Sugestie (trending / polecane) niezależnie od zakładki — żeby rail nie pustoszał poza „Społecznością".
    getDiscoverFeed()
      .then(r => setSuggest(r.data))
      .catch(() => undefined);
    // Dane rail (Twoje grupy / aktywne głosowania) na starcie TYLKO gdy startowa zakładka to nie Społeczność —
    // bo efekt per-zakładka poniżej i tak pobiera ten sam feed z sidebar. Inaczej to samo ciężkie
    // wall_feed(community) leciałoby podwójnie i spowalniało wejście na Społeczność.
    if (tab !== "spolecznosc") {
      getGroupsWallFeed(null, "community", { includeGroups: true, includeTopics: true })
        .then(r => {
          setMyGroups(r.data.sidebar?.my_groups ?? []);
          setActiveVotings(r.data.sidebar?.active_votings ?? []);
        })
        .catch(() => undefined);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Ładowanie per-zakładka (globalny loading). Feed czyta bieżące filtry, ale NIE reaguje na ich zmianę tutaj.
  useEffect(() => {
    let active = true;
    setLoading(true);
    const run = async () => {
      try {
        if (tab === "spolecznosc") {
          const feedR = await getGroupsWallFeed(null, feedScope, { includeGroups, includeTopics });
          if (!active) return;
          setFeedPosts((feedR.data.posts ?? []) as FeedPost[]);
          setMyGroups(feedR.data.sidebar?.my_groups ?? []);
          setActiveVotings(feedR.data.sidebar?.active_votings ?? []);
          setFeedCursor(feedR.data.next_cursor ?? null);
          setFeedHasMore(!!feedR.data.has_more);
        } else if (tab === "glosowania") {
          const r = await getPublicVotings({ per_page: 60, status: [VOTING_STATUSES.ONGOING, VOTING_STATUSES.PLANNED], exclude_own: true });
          if (active) setVotings((r.data?.votings ?? []) as ApiVotingListItem[]);
        } else if (tab === "grupy") {
          const r = await getDiscoverGroups({ q: debounced || undefined, sort });
          if (active) setGroups(r.data.groups);
        } else {
          const r = await getDiscoverProfiles({ q: debounced || undefined, sort, type: tab === "organizacje" ? "organization" : "user" });
          if (active) setProfiles(r.data.profiles);
        }
      } catch {
        /* ciche — pusty stan */
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
    // eslint-disable-next-line
  }, [tab, debounced, sort]);

  // Zmiana filtrów feedu (Wszystko/Obserwowani, Grupy/Tematy) — odświeża TYLKO posty, bez migotania całości.
  useEffect(() => {
    if (firstFilter.current) {
      firstFilter.current = false;
      return;
    }
    if (tab !== "spolecznosc") return;
    let active = true;
    setFeedBusy(true);
    getGroupsWallFeed(null, feedScope, { includeGroups, includeTopics })
      .then(r => {
        if (!active) return;
        setFeedPosts((r.data.posts ?? []) as FeedPost[]);
        setMyGroups(r.data.sidebar?.my_groups ?? []);
        setActiveVotings(r.data.sidebar?.active_votings ?? []);
        setFeedCursor(r.data.next_cursor ?? null);
        setFeedHasMore(!!r.data.has_more);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setFeedBusy(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line
  }, [feedScope, includeGroups, includeTopics]);

  const loadMoreFeed = () => {
    if (loadingMoreRef.current || !feedCursor) return;
    loadingMoreRef.current = true;
    setFeedLoadingMore(true);
    getGroupsWallFeed(feedCursor, feedScope, { includeGroups, includeTopics })
      .then(r => {
        setFeedPosts(prev => {
          const seen = new Set(prev.map(p => p.id));
          return [...prev, ...((r.data.posts ?? []) as FeedPost[]).filter(p => !seen.has(p.id))];
        });
        setFeedCursor(r.data.next_cursor ?? null);
        setFeedHasMore(!!r.data.has_more);
      })
      .catch(() => setFeedHasMore(false))
      .finally(() => {
        loadingMoreRef.current = false;
        setFeedLoadingMore(false);
      });
  };

  useEffect(() => {
    if (tab !== "spolecznosc" || !feedHasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(e => { if (e[0]?.isIntersecting) loadMoreFeed(); }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line
  }, [tab, feedHasMore, feedCursor]);

  const updatePost = (postId: string, updater: (p: WallPost) => WallPost) =>
    setFeedPosts(prev => prev.map(p => (p.id === postId ? ({ ...updater(p), group: p.group, can_interact: p.can_interact }) : p)));

  const filteredVotings = useMemo(
    () => votings.filter(v => (v.title ?? "").toLowerCase().includes(debounced.toLowerCase())),
    [votings, debounced]
  );

  const showSearch = tab !== "spolecznosc";
  const showSort = tab === "grupy" || tab === "organizacje" || tab === "ludzie";

  // ── główna kolumna wg zakładki ──
  const feedTab = (
    <VStack align="stretch" spacing="14px" w="100%">
      {me.canPost && <PortalComposer name={me.name} avatarUrl={me.avatarUrl} verified={me.verified} onPosted={p => setFeedPosts(prev => [p, ...prev])} />}

      {loggedIn && !me.canPost && (
        <Flex align="center" gap="12px" p="14px 16px" borderRadius="18px" sx={GLASS}>
          <Flex w="42px" h="42px" borderRadius="13px" bg={drgba("#1d9bf0", 0.14)} align="center" justify="center" flexShrink={0}>
            <Icon as={PiSealCheckDuotone} color="#1d9bf0" boxSize="22px" />
          </Flex>
          <Box>
            <Text fontWeight="800" color="#241536" fontSize="sm">Zweryfikuj konto, aby pisać posty publiczne</Text>
            <Text fontSize="xs" color="#6b6480">Posty w społeczności mogą tworzyć tylko zweryfikowane konta.</Text>
          </Box>
        </Flex>
      )}

      {/* Filtr feedu */}
      <Flex align="center" gap="12px" wrap="wrap">
        <HStack spacing="4px" p="4px" borderRadius="full" sx={GLASS} w="fit-content">
          {([
            { v: "community", label: "Wszystko" },
            { v: "following", label: "Obserwowani" }
          ] as const).map(o => (
            <Button
              key={o.v}
              size="sm"
              borderRadius="full"
              fontWeight="800"
              px="16px"
              onClick={() => setFeedScope(o.v)}
              color={feedScope === o.v ? "white" : "#6b6480"}
              bg={feedScope === o.v ? "primary" : "transparent"}
              _hover={{ bg: feedScope === o.v ? "#360079" : "rgba(24,12,48,0.05)" }}
            >
              {o.label}
            </Button>
          ))}
        </HStack>

        <HStack spacing="16px" px="16px" py="9px" borderRadius="full" sx={GLASS} w="fit-content">
          <HStack spacing="7px">
            <Switch size="sm" colorScheme="purple" isChecked={includeGroups} onChange={e => { const on = e.target.checked; setIncludeGroups(on); if (!on) setIncludeTopics(false); }} />
            <Text fontSize="13px" color="#5a5468" fontWeight="700">Grupy</Text>
          </HStack>
          <HStack spacing="7px" opacity={includeGroups ? 1 : 0.4}>
            <Switch size="sm" colorScheme="purple" isChecked={includeTopics} isDisabled={!includeGroups} onChange={e => setIncludeTopics(e.target.checked)} />
            <Text fontSize="13px" color="#5a5468" fontWeight="700">Tematy</Text>
          </HStack>
        </HStack>
      </Flex>

      {feedBusy && (
        <Flex justify="center" py="4px"><Spinner size="sm" color="primary" thickness="2px" /></Flex>
      )}

      <Box opacity={feedBusy ? 0.45 : 1} pointerEvents={feedBusy ? "none" : "auto"} transition="opacity 0.15s ease">
        {feedPosts.length === 0 ? (
          <Flex direction="column" align="center" justify="center" py="60px" borderRadius="22px" sx={GLASS}>
            <Flex w="60px" h="60px" borderRadius="17px" bg={drgba("#430098", 0.1)} align="center" justify="center" mb="14px">
              <Icon as={PiChatCircleDotsDuotone} boxSize="30px" color="primary" />
            </Flex>
            <Text color="#241536" fontWeight="900" fontSize="18px" mb="4px" letterSpacing="-0.01em">Tu pojawi się Twój feed</Text>
            <Text color="#6b6480" fontSize="14px" textAlign="center" maxW="400px">
              Posty z Twoich grup i z grup publicznych osób, które obserwujesz. Dołącz do grup i obserwuj ludzi, aby to ożywić.
            </Text>
          </Flex>
        ) : (
          <VStack align="stretch" spacing="14px">
            {feedPosts.map(p => (
              <WallPostCard
                key={p.id}
                post={p}
                groupId={p.group?.id ?? ""}
                isModerator={false}
                groupContext={p.group}
                canInteract={p.can_interact !== false}
                onChange={updater => updatePost(p.id, updater)}
                onRemove={() => setFeedPosts(prev => prev.filter(x => x.id !== p.id))}
              />
            ))}
            {feedHasMore && (
              <Flex ref={sentinelRef} justify="center" py="20px">{feedLoadingMore && <Spinner color="primary" thickness="3px" />}</Flex>
            )}
          </VStack>
        )}
      </Box>
    </VStack>
  );

  const main = loading ? (
    tab === "spolecznosc" ? <FeedSkeleton count={3} /> : <CardsGridSkeleton count={6} columns={{ base: 1, md: 2 }} />
  ) : tab === "spolecznosc" ? (
    feedTab
  ) : tab === "glosowania" ? (
    filteredVotings.length === 0 ? (
      <Text color="#6b6480" textAlign="center" py="60px">Brak głosowań.</Text>
    ) : (
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px">{filteredVotings.map(votingToTile)}</SimpleGrid>
    )
  ) : tab === "grupy" ? (
    groups.length === 0 ? (
      <Text color="#6b6480" textAlign="center" py="60px">Brak publicznych grup.</Text>
    ) : (
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">{groups.map(g => <GroupCard key={g.id} g={g} />)}</SimpleGrid>
    )
  ) : profiles.length === 0 ? (
    <Text color="#6b6480" textAlign="center" py="60px">Brak publicznych {tab === "organizacje" ? "organizacji" : "profili"}.</Text>
  ) : (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">{profiles.map(p => <ProfileCard key={p.id} p={p} />)}</SimpleGrid>
  );

  // ── kontekstowy rail (tylko zalogowany) ──
  const railInner = (
    <>
      {loggedIn && me.name && (
        <>
          <ProfileMini name={me.name} avatarUrl={me.avatarUrl} verified={me.verified} profileRef={me.profileRef} isOrg={me.isOrg} />
          <Box borderTop="1px solid rgba(67,0,152,0.08)" my="18px" />
        </>
      )}

      {loggedIn && (
        <>
          <RailHeading icon={PiUsersThreeDuotone} title="Twoje grupy" action={<Button as={Link} to="/organizator/grupy/nowa" variant="link" size="xs" color="primary" fontWeight="800" leftIcon={<Icon as={PiPlusBold} boxSize="11px" />}>Nowa</Button>} />
          {myGroups.length === 0 ? (
            <Text color="#a8a2b6" fontSize="13px" mb="4px">Nie należysz jeszcze do żadnej grupy.</Text>
          ) : (
            <VStack align="stretch" spacing="1px">{myGroups.slice(0, 6).map(g => <MyGroupRow key={g.id} g={g} />)}</VStack>
          )}
          {myGroups.length > 6 && (
            <Button as={Link} to="/organizator/moje-grupy" variant="ghost" size="sm" w="100%" mt="6px" color="primary" fontWeight="700" rightIcon={<Icon as={PiCaretRightBold} boxSize="12px" />}>Wszystkie</Button>
          )}
        </>
      )}

      {activeVotings.length > 0 && (
        <>
          <Box borderTop="1px solid rgba(67,0,152,0.08)" my="20px" />
          <RailHeading icon={PiBroadcastDuotone} title="Aktywne głosowania" color="#1f9e18" />
          <VStack align="stretch" spacing="1px">{activeVotings.map(v => <SideVotingRow key={v.id} v={v} />)}</VStack>
        </>
      )}

      {suggest && suggest.trending_votings.length > 0 && (
        <>
          <Box borderTop="1px solid rgba(67,0,152,0.08)" my="20px" />
          <RailHeading icon={PiFireDuotone} title="Na czasie" color="#e51f44" action={<Button variant="link" size="xs" color="primary" fontWeight="800" onClick={() => setTab("glosowania")}>Więcej</Button>} />
          <VStack align="stretch" spacing="1px">{suggest.trending_votings.slice(0, 5).map(v => <RailVotingRow key={v.id} v={v} />)}</VStack>
        </>
      )}

      {suggest && suggest.following_votings.length > 0 && (
        <>
          <Box borderTop="1px solid rgba(67,0,152,0.08)" my="20px" />
          <RailHeading icon={PiHeartDuotone} title="Od obserwowanych" color="#7b2ff7" />
          <VStack align="stretch" spacing="1px">{suggest.following_votings.slice(0, 5).map(v => <RailVotingRow key={v.id} v={v} />)}</VStack>
        </>
      )}

      {suggest && suggest.suggested_groups.length > 0 && (
        <>
          <Box borderTop="1px solid rgba(67,0,152,0.08)" my="20px" />
          <RailHeading icon={PiSparkleDuotone} title="Polecane grupy" color="#7b2ff7" action={<Button variant="link" size="xs" color="primary" fontWeight="800" onClick={() => setTab("grupy")}>Więcej</Button>} />
          <VStack align="stretch" spacing="4px">{suggest.suggested_groups.slice(0, 3).map(g => <GroupCard key={g.id} g={g} compact />)}</VStack>
        </>
      )}

      {suggest && suggest.suggested_profiles.length > 0 && (
        <>
          <Box borderTop="1px solid rgba(67,0,152,0.08)" my="20px" />
          <RailHeading icon={PiSparkleDuotone} title="Polecane konta" color="#e51f44" action={<Button variant="link" size="xs" color="primary" fontWeight="800" onClick={() => setTab("ludzie")}>Więcej</Button>} />
          <VStack align="stretch" spacing="10px">{suggest.suggested_profiles.slice(0, 3).map(p => <ProfileCard key={p.id} p={p} />)}</VStack>
        </>
      )}
    </>
  );

  const hasRail = loggedIn;
  // Jedna, spójna szerokość dla wszystkich zakładek — brak skoku przy przełączaniu, nagłówek wyrównany do treści.
  const contentMaxW = "940px";
  // Rail: dopóki nic nie dociągnięte — skeleton; potem realna treść.
  const railLoading = !suggest && myGroups.length === 0 && activeVotings.length === 0;
  const railContent = railLoading ? <RailSkeleton /> : railInner;

  return (
    <AdaptiveShell>
      <Flex align="flex-start">
        <Box flex="1" minW={0}>
          <Box w="100%" maxW={contentMaxW} mx="auto" px={{ base: "18px", md: "34px" }} py={{ base: "22px", md: "32px" }}>
            <VStack align="stretch" spacing="18px">
              <HubHero
                tab={tab}
                onTab={setTab}
                search={showSearch ? { value: search, onChange: setSearch } : undefined}
                sort={showSort ? { value: sort, onChange: setSort } : undefined}
              />

              {main}

              {/* Rail zwinięty do głównej kolumny na węższych ekranach */}
              {hasRail && (
                <Box display={{ base: "block", xl: "none" }} borderRadius="22px" overflow="hidden" position="relative" px="20px" py="22px" sx={GLASS}>
                  {railContent}
                </Box>
              )}
            </VStack>
          </Box>
        </Box>

        {/* Pływający prawy panel glass (tylko zalogowany, jak na pulpicie) */}
        {hasRail && (
          <Box as="aside" display={{ base: "none", xl: "block" }} w="360px" flexShrink={0} alignSelf="stretch">
            <Box position="sticky" top="0" h="100vh" p="12px">
              <Box position="relative" h="100%" borderRadius="26px" overflow="hidden" sx={GLASS}>
                <Box position="absolute" inset={0} pointerEvents="none" sx={{ background: "radial-gradient(58% 40% at 50% 22%, rgba(124,58,237,0.15), transparent 72%), radial-gradient(52% 40% at 50% 112%, rgba(37,99,235,0.1), transparent 72%)" }} />
                <VStack
                  position="relative"
                  align="stretch"
                  spacing="0"
                  h="100%"
                  px="20px"
                  py="26px"
                  sx={{
                    ...SCROLL_FOG,
                    maskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 26px), transparent 100%)",
                    WebkitMaskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 26px), transparent 100%)"
                  }}
                >
                  {railContent}
                </VStack>
              </Box>
            </Box>
          </Box>
        )}
      </Flex>
    </AdaptiveShell>
  );
};

export default DiscoverView;
